<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcWorksheetHeader',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save', 'deleted']);

  const props = defineProps({
    ...userComponentProps,
    worksheet: {
      type: Object,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const messages = ref({
    typeName: $fn.value.pgettext('Worksheet', 'Worksheet'),
    newTransaction: $fn.value.pgettext('Worksheet', 'New Worksheet'),
  });
  const ux = ref([]);

  const $data = computed(() => ({
    messages: messages.value,
    ux: ux.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'messages' }, { name: 'ux' }],
    loaders: {},
    ux: {},
    dataRefs: { messages, ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const readonly = computed(() => {
    return props.worksheet.locked;
  });
  const isNew = computed(() => {
    return props.worksheet.isNew;
  });
  const resourceTitle = computed(() => {
    if (loaded.value) {
      return messages.value.typeName;
    }
    return null;
  });
  const resourceKey = computed(() => {
    if (loaded.value) {
      return isNew.value
        ? messages.value.newTransaction
        : props.worksheet.snapshot('number');
    }
    return null;
  });

  watch(
    () => props.worksheet,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('worksheet');
    }
  );

  const act_6424600534600827_emit_x6jae3ow3_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const inst_evh_6424600534600827_close_g83c6ghoc_getWhen = async ($event) => {
    return $event.data.modalResult.ok;
  };
  const act_6424600534600827_showModal_4b6b3vfur_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'WorksheetEditor',
      props: {
        worksheet: _.cloneDeep(props.worksheet),
      },
    };
  };
  const act_8249915319319009_emit_931ewao2u_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const act_1890060294221449_emit_09srik1td_getActionArgs = async ($event) => {
    return {
      event: 'deleted',
    };
  };

  const act_6424600534600827_emit_x6jae3ow3 = new actionTypes.EmitAction({
    actionArgs: act_6424600534600827_emit_x6jae3ow3_getActionArgs,
  });
  const inst_evh_6424600534600827_close_g83c6ghoc =
    new actionTypes.EventHandlerImpl({
      action: act_6424600534600827_emit_x6jae3ow3,
      event: 'close',
      when: inst_evh_6424600534600827_close_g83c6ghoc_getWhen,
      displayName: 'emit',
    });
  const act_6424600534600827_showModal_4b6b3vfur =
    new actionTypes.ShowModalAction({
      actionArgs: act_6424600534600827_showModal_4b6b3vfur_getActionArgs,
      events: [inst_evh_6424600534600827_close_g83c6ghoc],
    });
  const inst_evh_6424600534600827_clickPreventStop_kz80k29bf =
    new actionTypes.EventHandlerImpl({
      action: act_6424600534600827_showModal_4b6b3vfur,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_6424600534600827_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_6424600534600827_clickPreventStop_kz80k29bf],
      event: 'click.prevent.stop',
    });
  const act_8249915319319009_emit_931ewao2u = new actionTypes.EmitAction({
    actionArgs: act_8249915319319009_emit_931ewao2u_getActionArgs,
    displayName: 'reload',
  });
  const inst_evh_8249915319319009_clickPreventStop_xk21xyz1n =
    new actionTypes.EventHandlerImpl({
      action: act_8249915319319009_emit_931ewao2u,
      event: 'click.prevent.stop',
      displayName: 'reload',
    });
  const inst_evh_8249915319319009_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_8249915319319009_clickPreventStop_xk21xyz1n],
      event: 'click.prevent.stop',
    });
  const act_1890060294221449_emit_09srik1td = new actionTypes.EmitAction({
    actionArgs: act_1890060294221449_emit_09srik1td_getActionArgs,
  });
  const inst_evh_1890060294221449_success_pf9da6pdx =
    new actionTypes.EventHandlerImpl({
      action: act_1890060294221449_emit_09srik1td,
      event: 'success',
      displayName: 'emit',
    });
  const inst_evh_1890060294221449_success = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1890060294221449_success_pf9da6pdx],
    event: 'success',
  });

  const actions = {
    inst_evh_6424600534600827_close_g83c6ghoc,
    inst_evh_6424600534600827_clickPreventStop_kz80k29bf,
    inst_evh_6424600534600827_clickPreventStop,
    inst_evh_8249915319319009_clickPreventStop_xk21xyz1n,
    reload: inst_evh_8249915319319009_clickPreventStop_xk21xyz1n,
    inst_evh_8249915319319009_clickPreventStop,
    inst_evh_1890060294221449_success_pf9da6pdx,
    inst_evh_1890060294221449_success,
  };

  const evh_6424600534600827_close_g83c6ghoc = async (event, scope) => {
    return await actions.inst_evh_6424600534600827_close_g83c6ghoc.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6424600534600827_clickPreventStop_kz80k29bf = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_6424600534600827_clickPreventStop_kz80k29bf.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6424600534600827_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_6424600534600827_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8249915319319009_clickPreventStop_xk21xyz1n = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_8249915319319009_clickPreventStop_xk21xyz1n.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8249915319319009_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_8249915319319009_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1890060294221449_success_pf9da6pdx = async (event, scope) => {
    return await actions.inst_evh_1890060294221449_success_pf9da6pdx.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1890060294221449_success = async (event, scope) => {
    return await actions.inst_evh_1890060294221449_success.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpBox alignItems="center" class="items-spacing w-100" v-if="loaded">
    <ItpText tag="h5" capitalize muted class="mb-0">
      {{ resourceTitle }}
    </ItpText>
    <span class="items-spacing__separator"> &middot; </span>
    <ItpText tag="h4" :text="resourceKey" class="mb-0"> </ItpText>
    <div :title="gettext('Closed')" v-if="readonly">
      <ItpIcon :icon="$config.lockIcon"> </ItpIcon>
    </div>
    <div class="btn-toolbar ms-auto flex-nowrap gap-2">
      <ItpButton
        icon="material-symbols:edit-outline"
        :tooltip="gettext('Edit')"
        variant="light"
        v-if="!isNew && !worksheet.isReadOnly"
        @click.prevent.stop="evh_6424600534600827_clickPreventStop($event, {})"
      >
      </ItpButton>
      <ItpButton
        icon="fa-sync-alt"
        :tooltip="gettext('Reload')"
        :spinning="actions.reload.isRunning"
        variant="light"
        v-if="!isNew"
        @click.prevent.stop="evh_8249915319319009_clickPreventStop($event, {})"
      >
      </ItpButton>
      <ItpLink
        icon="bxs:file-pdf"
        :href="worksheet.preview_url"
        :tooltip="pgettext('Worksheet', 'Open PDF')"
        target="_blank"
        use-auth
        button
        variant="light"
        show-spinner
        no-download-link
        :text="gettext('Preview')"
        v-if="worksheet.preview_url"
      >
      </ItpLink>
      <BDropdown right variant="light" no-caret class="border" v-if="!isNew">
        <template #button-content>
          <ItpIcon fa="bars" class="mx-1"> </ItpIcon>
        </template>
        <ItpButton
          dropdown-item
          :text="pgettext('BtnCopyResource', 'Copy')"
          :tooltip="pgettext('Worksheet', 'Copy worksheet')"
          variant="default"
        >
        </ItpButton>
        <BtnDeleteResource
          no-icon
          dropdown-item
          :resource="worksheet"
          :tooltip="pgettext('Worksheet', 'Delete worksheet')"
          v-if="!isNew"
          @success="evh_1890060294221449_success($event, {})"
        >
        </BtnDeleteResource>
        <hr />
        <ItpButton
          dropdown-item
          :text="pgettext('BtnResourceHistory', 'History')"
          :tooltip="pgettext('Worksheet', 'Worksheet history')"
          variant="default"
        >
        </ItpButton>
      </BDropdown>
    </div>
  </ItpBox>
</template>
